/**
 * QuickBuy Search Manager
 * Handles GraphQL-based search for products by SKU and Title
 */

class QuickBuySearchManager {
  constructor(options = {}) {
    this.form = options.form;
    this.input = options.input;
    this.onSearch = options.onSearch || (() => {});
    this.onReset = options.onReset || (() => {});
    this.filtersManager = options.filtersManager || null;
    
    this.currentSearchQuery = '';
    this.searchTimeout = null;
    
    this.init();
  }

  /**
   * Initialize search event listeners
   */
  init() {
    if (!this.form || !this.input) return;

    // Search form submit
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.performSearch();
    });

    // Reset button
    this.form.addEventListener('reset', (e) => {
      clearTimeout(this.searchTimeout); // Clear any pending search
      this.currentSearchQuery = '';
      this.input.value = '';
      this.onReset();
    });

    // Real-time search as user types (with debounce)
    this.input.addEventListener('input', () => {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 500); // Wait 500ms after user stops typing
    });
  }

  /**
   * Perform search
   */
  performSearch() {
    const query = this.input.value.trim();
    
    if (query === this.currentSearchQuery) {
      return; // No change, skip
    }
    
    const wasEmpty = this.currentSearchQuery === '';
    const isNowSearching = query.length > 0;
    
    // If starting a new search (was empty, now has query), clear filters first
    if (wasEmpty && isNowSearching && this.filtersManager) {
      if (this.filtersManager.hasActiveFilters()) {
        this.filtersManager.clearFiltersSilent();
      }
      
      // If date sorting is active, reset to Title A-Z
      const currentSort = this.filtersManager.getSortKey();
      if (currentSort === 'CREATED' || currentSort === 'CREATED_DESC') {
        this.filtersManager.resetSortToTitle();
      }
    }
    
    this.currentSearchQuery = query;
    this.onSearch(query);
  }

  /**
   * Get current search query
   */
  getSearchQuery() {
    return this.currentSearchQuery;
  }

  /**
   * Build search query string for Storefront API `products(query: ...)`.
   * Leading `*term*` is a poor match for variant SKU (Shopify uses prefix style `term*` for prefix search).
   * For compact codes (SKU/EAN-style), combine bare term + prefix + title prefix.
   * @see https://shopify.dev/docs/api/usage/search-syntax
   */
  buildSearchQueryString(collectionHandle = null) {
    if (!this.currentSearchQuery) return '';

    const raw = this.currentSearchQuery.trim();
    const escaped = String(raw).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const looksLikeCode = /^[A-Za-z0-9._\-]+$/.test(raw) && raw.length <= 80;

    let searchQuery;
    if (looksLikeCode) {
      searchQuery = `(${escaped} OR ${escaped}* OR title:${escaped}*)`;
    } else {
      searchQuery = `*${escaped.replace(/[()]/g, '')}*`;
    }

    if (collectionHandle) {
      searchQuery = `${searchQuery} AND available_for_sale:true`;
    }

    return searchQuery;
  }

  /**
   * Get search query parameter for GraphQL
   */
  getSearchQueryParam(collectionHandle = null) {
    const searchString = this.buildSearchQueryString(collectionHandle);
    return searchString ? `, query: "${searchString}"` : '';
  }

  /**
   * Clear search
   */
  clearSearch() {
    this.currentSearchQuery = '';
    this.input.value = '';
    this.onReset();
  }

  /**
   * Clear search silently (without triggering callbacks)
   * Used when filters are applied and we want to clear search without reload
   */
  clearSearchSilent() {
    clearTimeout(this.searchTimeout);
    this.currentSearchQuery = '';
    this.input.value = '';
  }

  /**
   * Set search query programmatically
   */
  setSearchQuery(query) {
    this.currentSearchQuery = query;
    this.input.value = query;
    this.onSearch(query);
  }

  /**
   * Check if search is active
   */
  isSearchActive() {
    return this.currentSearchQuery.length > 0;
  }
}

// Export to global scope
window.QuickBuySearchManager = QuickBuySearchManager;
